import { type ChangeEvent, useLayoutEffect, useMemo, useState } from 'react'

const initSelected = () => new Set<string>()
const toggleSet = <S>(set: Set<S>, id: S) =>
	set.has(id) ? set.delete(id) : set.add(id)

interface WithId {
	id: string
}
export function useSelect({
	items,
	total
}: { items: WithId[]; total: number }) {
	const selected = useMemo(initSelected, [])
	const [areAllSelected, setAreAllSelected] = useState(false)
	const [_, setRerender] = useState(0)

	const handleRowCheck = (id: string) => {
		toggleSet(selected, id)
		recheck()
	}

	function removeId(id: string) {
		selected.delete(id)
		recheck()
	}

	function clearAll() {
		selected.clear()
		setAreAllSelected(false)
		setRerender(prev => ++prev)
	}

	const addAllFromPage = () => items.forEach(item => selected.add(item.id))

	function recheck() {
		setAreAllSelected(selected.size === total)
		setRerender(prev => ++prev)
	}

	function handleSelectAll() {
		addAllFromPage()
		setAreAllSelected(true)
		setRerender(prev => ++prev)
	}

	function handleChangeCheckbox(event: ChangeEvent<HTMLInputElement>) {
		if (!event.target.checked) {
			return clearAll()
		}

		addAllFromPage()
		recheck()
	}

	useLayoutEffect(() => {
		if (areAllSelected) {
			items.forEach(item => selected.add(item.id))
		}
	}, [areAllSelected, items, selected])

	return {
		areAllSelected,
		clearAll,
		handleChangeCheckbox,
		handleRowCheck,
		handleSelectAll,
		removeId,
		selected
	}
}
